/**
 * Utility functions for identifying button types in Adonis CTA buttons
 */

export type ButtonType =
  | "booking"
  | "contact-phone"
  | "contact-email"
  | "general";

/**
 * Normalizes a URL by removing query parameters and trailing slashes
 */
function normalizeUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    // Return origin + pathname without query params or hash
    return `${urlObj.origin}${urlObj.pathname}`.replace(/\/$/, "");
  } catch {
    // If URL parsing fails, just return the original
    return url.replace(/\/$/, "");
  }
}

/**
 * Normalizes a phone number by removing common formatting characters
 */
function normalizePhoneNumber(phone: string): string {
  return phone.replace(/[\s\-()+]/g, "");
}

/**
 * Checks if the href matches the booking URL (ignoring query params)
 */
export function matchesBookingUrl(
  href: string | undefined,
  bookingUrl: string | null | undefined
): boolean {
  if (!href || !bookingUrl) return false;

  // Handle mailto: and tel: prefixes
  if (href.startsWith("tel:") || href.startsWith("mailto:")) {
    return false;
  }

  try {
    const normalizedHref = normalizeUrl(href);
    const normalizedBooking = normalizeUrl(bookingUrl);
    return normalizedHref === normalizedBooking;
  } catch {
    return false;
  }
}

/**
 * Checks if the href is a phone contact that matches the platform phone number
 */
export function isContactPhone(
  href: string | undefined,
  platformPhone: string | null | undefined
): boolean {
  if (!href || !platformPhone) return false;

  if (!href.startsWith("tel:")) return false;

  const hrefPhone = normalizePhoneNumber(href.replace("tel:", ""));
  const platformPhoneNormalized = normalizePhoneNumber(platformPhone);

  return hrefPhone === platformPhoneNormalized;
}

/**
 * Checks if the href is an email contact that matches the platform email
 */
export function isContactEmail(
  href: string | undefined,
  platformEmail: string | null | undefined
): boolean {
  if (!href || !platformEmail) return false;

  if (!href.startsWith("mailto:")) return false;

  const hrefEmail = href.replace("mailto:", "").toLowerCase().trim();
  const platformEmailNormalized = platformEmail.toLowerCase().trim();

  return hrefEmail === platformEmailNormalized;
}

/**
 * Determines the button type based on the href and platform location data
 */
export function getButtonType(
  href: string | undefined,
  platformLocation?: {
    bookAppointmentUrl?: string | null;
    phoneNumber?: string | null;
    email?: string | null;
  }
): ButtonType {
  if (!href || !platformLocation) return "general";

  if (matchesBookingUrl(href, platformLocation.bookAppointmentUrl)) {
    return "booking";
  }

  if (isContactPhone(href, platformLocation.phoneNumber)) {
    return "contact-phone";
  }

  if (isContactEmail(href, platformLocation.email)) {
    return "contact-email";
  }

  return "general";
}
